import React, { useState, useEffect } from 'react';
import { Window, Button, TextInput, Radio, Checkbox, Fieldset } from 'react95';
import styles from '../styles/modules/Window.module.css';
import { t } from '../data/translations';
import { rsvpService, RSVPResponse } from '../services/rsvpService';
import { authService } from '../services/authService';

interface WindowRSVPProps {
  onClose: () => void;
}

interface FormData {
  name: string;
  attendance: 'yes' | 'no' | 'maybe';
  day1: boolean;
  day2: boolean;
  overnight: 'один день' | 'два дня' | 'нет';
  plus_one: boolean;
  has_child: boolean;
  alcohol_preferences: string;
  message: string;
}

const WindowRSVP: React.FC<WindowRSVPProps> = ({ onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    attendance: 'yes',
    day1: true,
    day2: true,
    overnight: 'нет',
    plus_one: false,
    has_child: false,
    alcohol_preferences: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [existingRSVP, setExistingRSVP] = useState<RSVPResponse | null>(null);

  // Проверяем, есть ли уже RSVP для текущего пользователя
  useEffect(() => {
    const checkExistingRSVP = async () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        // Предзаполняем имя пользователя
        setFormData(prev => ({
          ...prev,
          name: currentUser.name
        }));

        const response = await rsvpService.getRSVPByKey(currentUser.secretKey);
        if (response.success && response.data) {
          setExistingRSVP(response.data);
        }
      }
    };

    checkExistingRSVP();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRadioChange = (value: 'yes' | 'no' | 'maybe') => {
    setFormData({
      ...formData,
      attendance: value
    });
  };

  const handleDayChange = (day: 'day1' | 'day2', checked: boolean) => {
    setFormData({
      ...formData,
      [day]: checked
    });
  };

  const handleOvernightChange = (value: 'один день' | 'два дня' | 'нет') => {
    setFormData({
      ...formData,
      overnight: value
    });
  };

  const handleBooleanChange = (field: 'plus_one' | 'has_child', value: boolean) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        setError('Пользователь не авторизован');
        return;
      }

      const rsvpData = {
        name: formData.name,
        will_attend: formData.attendance === 'yes',
        day1: formData.day1,
        day2: formData.day2,
        overnight: formData.overnight,
        plus_one: formData.plus_one,
        has_child: formData.has_child,
        alcohol_preferences: formData.alcohol_preferences || undefined,
        comment: formData.message || undefined,
        secret_key: currentUser.secretKey
      };

      const response = await rsvpService.submitRSVP(rsvpData);
      
      if (response.success) {
        setIsSuccess(true);
      } else {
        setError(response.error || t('windows.rsvp.errorMessage'));
      }
    } catch (err) {
      setError(t('windows.rsvp.errorMessage'));
      console.error('Error submitting RSVP:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Window className={styles.window}>
      <div className={styles.windowHeader}>
        <span>{t('windows.rsvp.title')}</span>
        <Button onClick={onClose} size="sm">
          <span>✕</span>
        </Button>
      </div>
      <div className={styles.windowContent}>
        {isSuccess ? (
          <div className={styles.successMessage}>
            <h2>{t('windows.rsvp.successTitle')}</h2>
            <p>{t('windows.rsvp.successMessage')}</p>
            <p>{t('windows.rsvp.successFooter')}</p>
            <Button onClick={onClose}>{t('common.close')}</Button>
          </div>
        ) : existingRSVP ? (
          <div className={styles.successMessage}>
            <h2>Спасибо!</h2>
            <p>Вы уже отправили ответ на приглашение.</p>
            <p>Увидимся на празднике! 🎉</p>
            <Button onClick={onClose}>{t('common.close')}</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.rsvpForm}>
            <div className={styles.formField}>
              <Fieldset label={t('windows.rsvp.nameLabel')}>
                <TextInput
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('windows.rsvp.namePlaceholder')}
                  required
                />
              </Fieldset>
            </div>

            <div className={styles.formField}>
              <Fieldset label={t('windows.rsvp.attendanceLabel')}>
                <div className={styles.radioGroup}>
                  <Radio
                    checked={formData.attendance === 'yes'}
                    onChange={() => handleRadioChange('yes')}
                    label={t('windows.rsvp.attendanceYes')}
                    name="attendance"
                    value="yes"
                  />
                  <Radio
                    checked={formData.attendance === 'no'}
                    onChange={() => handleRadioChange('no')}
                    label={t('windows.rsvp.attendanceNo')}
                    name="attendance"
                    value="no"
                  />
                  <Radio
                    checked={formData.attendance === 'maybe'}
                    onChange={() => handleRadioChange('maybe')}
                    label={t('windows.rsvp.attendanceMaybe')}
                    name="attendance"
                    value="maybe"
                  />
                </div>
              </Fieldset>
            </div>

            {formData.attendance === 'yes' && (
              <>
                <div className={styles.formField}>
                  <Fieldset label={t('windows.rsvp.daysLabel')}>
                    <div className={styles.checkboxGroup}>
                      <Checkbox
                        checked={formData.day1}
                        onChange={(e) => handleDayChange('day1', e.target.checked)}
                        label={t('windows.rsvp.day1Label')}
                      />
                      <Checkbox
                        checked={formData.day2}
                        onChange={(e) => handleDayChange('day2', e.target.checked)}
                        label={t('windows.rsvp.day2Label')}
                      />
                    </div>
                  </Fieldset>
                </div>

                <div className={styles.formField}>
                  <Fieldset label={t('windows.rsvp.overnightLabel')}>
                    <div className={styles.radioGroup}>
                      <Radio
                        checked={formData.overnight === 'один день'}
                        onChange={() => handleOvernightChange('один день')}
                        label={t('windows.rsvp.overnightOneDay')}
                        name="overnight"
                        value="один день"
                      />
                      <Radio
                        checked={formData.overnight === 'два дня'}
                        onChange={() => handleOvernightChange('два дня')}
                        label={t('windows.rsvp.overnightTwoDays')}
                        name="overnight"
                        value="два дня"
                      />
                      <Radio
                        checked={formData.overnight === 'нет'}
                        onChange={() => handleOvernightChange('нет')}
                        label={t('windows.rsvp.overnightNo')}
                        name="overnight"
                        value="нет"
                      />
                    </div>
                  </Fieldset>
                </div>

                <div className={styles.formField}>
                  <Fieldset label={t('windows.rsvp.plusOneLabel')}>
                    <div className={styles.radioGroup}>
                                              <Radio
                          checked={formData.plus_one}
                          onChange={() => handleBooleanChange('plus_one', true)}
                          label={t('common.yes')}
                          name="plus_one"
                          value="true"
                        />
                        <Radio
                          checked={!formData.plus_one}
                          onChange={() => handleBooleanChange('plus_one', false)}
                          label={t('common.no')}
                          name="plus_one"
                          value="false"
                        />
                    </div>
                  </Fieldset>
                </div>

                <div className={styles.formField}>
                  <Fieldset label={t('windows.rsvp.hasChildLabel')}>
                    <div className={styles.radioGroup}>
                                              <Radio
                          checked={formData.has_child}
                          onChange={() => handleBooleanChange('has_child', true)}
                          label={t('common.yes')}
                          name="has_child"
                          value="true"
                        />
                        <Radio
                          checked={!formData.has_child}
                          onChange={() => handleBooleanChange('has_child', false)}
                          label={t('common.no')}
                          name="has_child"
                          value="false"
                        />
                    </div>
                  </Fieldset>
                </div>

                <div className={styles.formField}>
                  <Fieldset label={t('windows.rsvp.alcoholPreferencesLabel')}>
                    <TextInput
                      id="alcohol_preferences"
                      name="alcohol_preferences"
                      value={formData.alcohol_preferences}
                      onChange={handleChange}
                      placeholder={t('windows.rsvp.alcoholPreferencesPlaceholder')}
                    />
                  </Fieldset>
                </div>
              </>
            )}

            <div className={styles.formField}>
              <Fieldset label={t('windows.rsvp.messageLabel')}>
                <TextInput
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('windows.rsvp.messagePlaceholder')}
                  multiline
                  rows={3}
                />
              </Fieldset>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.formActions}>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className={styles.submitButton}
              >
                {isSubmitting ? t('windows.rsvp.submitting') : t('windows.rsvp.submitButton')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Window>
  );
};

export default WindowRSVP;
